import { styled } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import { Checkbox } from 'components/ui';

export const firmDocumentDialogErrorAlertSx: SxProps<Theme> = {
  alignItems: 'flex-start',
  borderWidth: 'var(--dialog-border-width)',
  gap: 1,
  maxHeight: 220,
  mt: 1.5,
  overflow: 'auto',
  p: 1,
  whiteSpace: 'pre-line',
  '& .MuiAlert-icon': {
    height: 'var(--dialog-description-line-height)',
    minWidth: 'var(--dialog-description-line-height)',
    width: 'var(--dialog-description-line-height)',
  },
  '& .MuiAlert-icon .MuiSvgIcon-root': {
    fontSize: 'var(--dialog-description-font-size)',
  },
  '& .MuiAlert-message': {
    fontSize: 'var(--dialog-description-font-size)',
    fontWeight: 500,
    lineHeight: 'var(--dialog-description-line-height)',
  },
};

export const FirmDocumentMoveDialogTarget = styled('span')(({ theme }) => ({
  display: 'inline',
  '& [data-firm-document-move-dialog-target-separator="true"]': {
    color: theme.palette.brand[500],
    fontWeight: 700,
    padding: theme.spacing(0, 0.5),
  },
}));

export const FirmDocumentDialogDocumentList = styled('span')(({ theme }) => ({
  color: theme.palette.neutral[700],
  display: '-webkit-box',
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: 'var(--dialog-description-line-height)',
  maxHeight: 'calc(var(--dialog-description-line-height) * 3)',
  overflow: 'hidden',
  overflowWrap: 'anywhere',
  textOverflow: 'ellipsis',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 3,
}));

export const FirmDocumentDialogDocumentTooltipList = styled('span')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(0.5),
  maxWidth: 320,
  whiteSpace: 'normal',
}));

export const FirmDocumentDialogDocumentTooltipItem = styled('span')({
  display: 'block',
  overflowWrap: 'anywhere',
});

export const FirmDocumentDialogScrollableDocumentList = styled('ol')(({ theme }) => ({
  backgroundColor: theme.palette.neutral[50],
  border: `var(--dialog-border-width) solid ${theme.palette.neutral[200]}`,
  borderRadius: theme.shape.radius.s,
  boxSizing: 'border-box',
  color: theme.palette.neutral[700],
  display: 'grid',
  gap: theme.spacing(0.75),
  margin: theme.spacing(1, 0, 0),
  maxHeight: 180,
  overflow: 'auto',
  padding: theme.spacing(1, 1.25, 1, 3),
}));

export const FirmDocumentDialogDocumentListItem = styled('li')(({ theme }) => ({
  fontSize: 'var(--dialog-description-font-size)',
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: 'var(--dialog-description-line-height)',
  overflowWrap: 'anywhere',
  paddingLeft: theme.spacing(0.25),
}));

export const FirmDocumentDialogSupportingText = styled('span')(({ theme }) => ({
  color: theme.palette.neutral[600],
  display: 'block',
  fontSize: 'var(--dialog-description-font-size)',
  lineHeight: 'var(--dialog-description-line-height)',
  marginTop: theme.spacing(1),
}));

export const FirmDocumentDialogField = styled('label')(({ theme }) => ({
  color: theme.palette.neutral[700],
  display: 'grid',
  fontSize: 'var(--dialog-description-font-size)',
  fontWeight: theme.typography.fontWeightMedium,
  gap: theme.spacing(0.75),
  lineHeight: 'var(--dialog-description-line-height)',
}));

export const FirmDocumentDialogFieldGroup = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(1.5),
}));

export const FirmDocumentDialogOptionList = styled('div')(({ theme }) => ({
  border: `var(--dialog-border-width) solid ${theme.palette.neutral[200]}`,
  borderRadius: theme.shape.radius.s,
  display: 'grid',
  gap: 0,
  maxHeight: 180,
  overflow: 'auto',
}));

export const FirmDocumentDialogOption = styled('button')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.common.white,
  border: 0,
  color: theme.palette.neutral[700],
  cursor: 'pointer',
  display: 'grid',
  fontSize: 'var(--dialog-description-font-size)',
  gap: theme.spacing(1),
  gridTemplateColumns: 'auto minmax(0, 1fr)',
  lineHeight: 'var(--dialog-description-line-height)',
  minHeight: 36,
  padding: theme.spacing(0.75, 1),
  textAlign: 'left',
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),
  '&:hover, &:focus-visible': {
    backgroundColor: theme.palette.neutral[100],
    color: theme.palette.neutral[800],
    outline: 0,
  },
  '&:disabled': {
    color: theme.palette.neutral[400],
    cursor: 'default',
  },
}));

export const FirmDocumentDialogOptionCheckbox = styled(Checkbox)({
  pointerEvents: 'none',
});

export const FirmDocumentDialogOptionText = styled('span')({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const FirmDocumentDialogHelperText = styled('span')(({ theme }) => ({
  color: theme.palette.neutral[500],
  fontSize: 'var(--dialog-description-font-size)',
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: 'var(--dialog-description-line-height)',
}));

export const FirmDocumentDialogFileInput = styled('input')(({ theme }) => ({
  border: `var(--dialog-border-width) solid ${theme.palette.neutral[300]}`,
  borderRadius: theme.shape.radius.s,
  boxSizing: 'border-box',
  color: theme.palette.neutral[700],
  fontSize: 'var(--dialog-description-font-size)',
  lineHeight: 'var(--dialog-description-line-height)',
  padding: theme.spacing(1),
  width: '100%',
  '&:focus-visible': {
    borderColor: theme.palette.brand[400],
    outline: 0,
  },
}));
